import { Column, DataType, InputType } from '@miq/catalog';

/**
 * L2-S5: moved verbatim out of
 * `@controls/advanced-controls/input-control/pipes/to-advanced-config.helper`, which is now a
 * re-export shim.
 *
 * `AgTableComponent` calls `AdvancedInputFactory.getInputType` to pick a cell editor, and the
 * component moved into this library — which may not import `@controls`. The helper itself is four
 * pure switch functions over three scalar fields, so relocating it is free.
 *
 * The one edit is the shape of {@link InputTypeInputConfigUnion}. It used to be
 * `Pick<Column & InputConfig, 'inputType' | 'dataType' | 'lookupCategory'>`, and `InputConfig` lives
 * in `@controls/advanced-controls/advanced-inputs.model` — a module that VALUE-imports nine
 * advanced-input component classes for its `InputTypeToAdvancedInputMap`, so even a type-only
 * reference is a trap for anyone who later drops the `type` keyword. `InputConfig` itself is
 * `Pick<Column, ...> & { inputType: InputType }`, so intersecting the `Column` pick with that one
 * narrowing reproduces the original type exactly, without the module edge.
 */
export type InputTypeInputConfigUnion = Pick<Column, 'inputType' | 'dataType' | 'lookupCategory'> & {
  inputType: InputType;
};

/**
 * Return AdvancedInput component based on DataType InputType and lookupCategory mix
 * @param inputType
 * @param lookupCategory
 */
export function getByDataAndInputType({ inputType, dataType, lookupCategory }: InputTypeInputConfigUnion): InputType {
  if (lookupCategory != null && inputType !== InputType.NAICS) {
    return InputType.Dropdown;
  }
  switch (`${inputType}-${dataType}`) {
    // CHECKBOX
    case `${InputType.Slider}-${DataType.Boolean}`:
      return InputType.Checkbox;
    // DATE PICKER
    case `${InputType.Date}-${DataType.String}`:
    case `${InputType.Date}-${DataType.Text}`:
    case `${InputType.Date}-${DataType.Date}`:
    case `${InputType.Date}-${DataType.Timestamp}`:
    case `${InputType.Date}-${DataType.TimestampWithTimeZone}`:
    case `${InputType.Text}-${DataType.Timestamp}`:
    case `${InputType.Text}-${DataType.TimestampWithTimeZone}`:
    case `${InputType.Text}-${DataType.Date}`:
      return InputType.Date;
    // NUMBER
    case `${InputType.Date}-${DataType.Id}`:
    case `${InputType.Date}-${DataType.Integer}`:
    case `${InputType.Date}-${DataType.Numeric}`:
    case `${InputType.Number}-${DataType.Month}`:
    case `${InputType.Number}-${DataType.Integer}`:
    case `${InputType.Number}-${DataType.Numeric}`:
    case `${InputType.Number}-${DataType.Id}`:
    case `${InputType.Number}-${DataType.Date}`:
    case `${InputType.Text}-${DataType.Numeric}`:
    case `${InputType.Text}-${DataType.Integer}`:
    case `${InputType.Slider}-${DataType.Numeric}`:
    case `${InputType.Slider}-${DataType.Integer}`:
      return InputType.Number;
    // TEXT
    case `${InputType.Text}-${DataType.Id}`:
    case `${InputType.Text}-${DataType.Text}`:
    case `${InputType.Slider}-${DataType.Text}`:
      return InputType.Text;
    // TEXT AREA
    case `${InputType.Text}-${DataType.TextArea}`:
      return InputType.TextArea;
    // DEFAULT TEXT
  }
  return null;
}

/**
 * Return AdvancedInput component based on InputType and lookupCategory
 * @param inputType
 * @param lookupCategory
 */
export function getByInputType({ inputType, lookupCategory }: InputTypeInputConfigUnion): InputType {
  switch (inputType) {
    // CHIPS
    case InputType.Chips:
    case InputType.StrictChips:
    case InputType.Array:
      return InputType.Chips;
    case InputType.NAICS:
      return InputType.NAICS;
    // Dropdown
    case InputType.Dropdown:
      return lookupCategory != null ? InputType.Dropdown : InputType.Text;
  }
  return null;
}

/**
 * Return AdvancedInput component based on DataType only
 * @param inputType
 * @param lookupCategory
 */
export function getByDataType({ dataType }: InputTypeInputConfigUnion): InputType {
  switch (dataType) {
    // CHIPS
    case DataType.Numeric:
      return InputType.Number;
    case DataType.TextArea:
      return InputType.TextArea;
    case DataType.TimestampWithTimeZone:
      return InputType.Date;
  }
  return null;
}

/**
 * AdvancedInputFactory
 */
export class AdvancedInputFactory {
  static getInputType(params: InputTypeInputConfigUnion): InputType {
    return (
      getByDataAndInputType(params) ||
      getByInputType(params) ||
      getByDataType(params) ||
      // defaulted to Text
      InputType.Text
    );
  }
}
